import {
  CheckOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  GlobalOutlined,
  IdcardOutlined,
  LogoutOutlined,
  SafetyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal } from "antd";
import type { MenuProps } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import type { SupportedLocale } from "../i18n";
import { useLanguageSwitch } from "./LanguageSwitcher";
import { SupportContacts, useSupportContacts } from "./SupportMenu";

const LANGUAGE_PREFIX = "language:";

/**
 * Alles, was zum eigenen Konto gehoert, unter dem Benutzernamen — wie im Menue
 * der alten Oberflaeche.
 *
 * Bis zum 13.09.2026 standen Konto, Datenschutz, Support, Sprache, Startseite
 * und Abmelden als einzelne Schaltflaechen nebeneinander in der Kopfzeile. Auf
 * dem Desktop passte das knapp, auf einem Telefon schob es alles rechts aus dem
 * Bild. Ein Menue braucht in jeder Breite nur einen Platz.
 *
 * `compact` zeigt auf schmalen Bildschirmen nur den Vornamen.
 */
export function UserMenu({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const language = useLanguageSwitch();
  const contacts = useSupportContacts();
  const [supportOpen, setSupportOpen] = useState(false);

  if (!user) return null;

  const items: MenuProps["items"] = [
    { key: "account", icon: <IdcardOutlined />, label: t("auth.edit_account") },
    { key: "privacy", icon: <SafetyOutlined />, label: t("user.privacy_policy_menu") },
    ...(contacts.length > 0
      ? [{ key: "support", icon: <CustomerServiceOutlined />, label: t("navigation.support") }]
      : []),
    {
      key: "language",
      icon: <GlobalOutlined />,
      label: t("activerecord.models.language"),
      children: language.options.map((option) => ({
        key: `${LANGUAGE_PREFIX}${option.value}`,
        label: option.label,
        // Ein Haken statt einer Hervorhebung: die Auswahl ist ein Zustand, kein
        // Ort, an dem man sich befindet.
        icon: option.value === language.selected ? <CheckOutlined /> : <span style={{ width: 14 }} />,
      })),
    },
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, label: t("auth.logout") },
  ];

  const onClick: MenuProps["onClick"] = ({ key }) => {
    if (key.startsWith(LANGUAGE_PREFIX)) {
      language.change(key.slice(LANGUAGE_PREFIX.length) as SupportedLocale);
      return;
    }

    switch (key) {
      case "account":
        navigate("/main/password");
        break;
      case "privacy":
        navigate("/privacy-policy");
        break;
      case "support":
        setSupportOpen(true);
        break;
      case "logout":
        void logout();
        break;
    }
  };

  return (
    <>
      {/* Untermenues oeffnen auf Klick, nicht beim Darueberfahren: auf einem
          Telefon gibt es kein Darueberfahren. */}
      <Dropdown
        menu={{ items, onClick, triggerSubMenuAction: "click" }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button type="text" icon={<UserOutlined />}>
          {compact ? user.first_name : `${user.first_name} ${user.last_name}`}
          <DownOutlined style={{ fontSize: 10 }} />
        </Button>
      </Dropdown>

      <Modal
        open={supportOpen}
        title={t("navigation.support")}
        footer={null}
        onCancel={() => setSupportOpen(false)}
      >
        <SupportContacts contacts={contacts} />
      </Modal>
    </>
  );
}
